/**
 * The single place that knows about GA4.
 *
 * No component imports gtag directly, so swapping to Plausible or anything
 * else later is a change to this file alone.
 *
 * Set the measurement ID in .env.local:
 *   VUE_APP_GA_ID=G-XXXXXXXXXX
 * With no ID configured, every call is a no-op (and logs in development), so
 * the site runs fine before the GA4 property exists.
 */

const GA_ID = process.env.VUE_APP_GA_ID || "";
const isDev = process.env.NODE_ENV !== "production";

let ready = false;

function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function initAnalytics() {
  if (!GA_ID || typeof window === "undefined") {
    if (isDev) {
      console.info("[analytics] no VUE_APP_GA_ID set — events are no-ops");
    }
    return;
  }

  window.dataLayer = window.dataLayer || [];
  gtag("js", new Date());
  // Router drives page_view, so turn off the automatic one.
  gtag("config", GA_ID, { send_page_view: false });

  // Loaded after first paint so it never competes with the hero render.
  const load = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
    ready = true;
  };

  if (document.readyState === "complete") {
    load();
  } else {
    window.addEventListener("load", load, { once: true });
  }
}

/**
 * Fire an event.
 * @param {string} event
 * @param {object} [params]
 */
export function track(event, params = {}) {
  if (isDev) {
    console.debug("[analytics]", event, params);
  }
  if (!GA_ID || !ready) return;
  gtag("event", event, params);
}

export function trackPageView(path) {
  track("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : "",
  });
}

/**
 * Fires once per threshold per page load. Returns a teardown function.
 */
export function trackScrollDepth() {
  if (typeof window === "undefined") return () => {};

  const thresholds = [25, 50, 75, 100];
  const seen = new Set();
  let queued = false;

  const measure = () => {
    queued = false;
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const percent = ((window.scrollY / scrollable) * 100).toFixed(0);
    thresholds.forEach((threshold) => {
      if (percent >= threshold && !seen.has(threshold)) {
        seen.add(threshold);
        track("scroll_depth", { percent: threshold });
      }
    });

    if (seen.size === thresholds.length) teardown();
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  };

  const teardown = () => window.removeEventListener("scroll", onScroll);

  window.addEventListener("scroll", onScroll, { passive: true });
  return teardown;
}

/** Convenience wrapper so CTAs all report the same shape. */
export function trackCta(location) {
  track("cta_click", { location });
}

export function useAnalytics() {
  return { track, trackCta, trackPageView };
}
